import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { SentryErrorBoundary } from '@/lib/sentry'
import { FullPageSpinner } from '@/components/ui/Spinner'

const Login = lazy(() => import('@/pages/Login').then((m) => ({ default: m.Login })))
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const BatchDetail = lazy(() => import('@/pages/BatchDetail').then((m) => ({ default: m.BatchDetail })))
const QAChecklist = lazy(() => import('@/pages/QAChecklist').then((m) => ({ default: m.QAChecklist })))
const Tracking = lazy(() => import('@/pages/Tracking').then((m) => ({ default: m.Tracking })))
const AuditLog = lazy(() => import('@/pages/AuditLog').then((m) => ({ default: m.AuditLog })))
const BulkProductLog = lazy(() => import('@/pages/BulkProductLog').then((m) => ({ default: m.BulkProductLog })))
const UserManagement = lazy(() => import('@/pages/UserManagement').then((m) => ({ default: m.UserManagement })))
const A2801K = lazy(() => import('@/pages/A2801K').then((m) => ({ default: m.A2801K })))
const A2801A = lazy(() => import('@/pages/A2801A').then((m) => ({ default: m.A2801A })))
const A2801C = lazy(() => import('@/pages/A2801C').then((m) => ({ default: m.A2801C })))
const QA134B = lazy(() => import('@/pages/QA134B').then((m) => ({ default: m.QA134B })))
const OutputLog = lazy(() => import('@/pages/OutputLog').then((m) => ({ default: m.OutputLog })))

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (!session) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (session) return <Navigate to="/" replace />
  return <>{children}</>
}

export function App() {
  return (
    <SentryErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<FullPageSpinner />}>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicOnly>
                  <Login />
                </PublicOnly>
              }
            />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/batches/:id"
              element={
                <RequireAuth>
                  <BatchDetail />
                </RequireAuth>
              }
            />
            <Route
              path="/batches/:id/checklist/:templateCode"
              element={
                <RequireAuth>
                  <QAChecklist />
                </RequireAuth>
              }
            />
            <Route
              path="/batches/:id/tracking"
              element={
                <RequireAuth>
                  <Tracking />
                </RequireAuth>
              }
            />
            <Route
              path="/batches/:id/bulk-log"
              element={
                <RequireAuth>
                  <BulkProductLog />
                </RequireAuth>
              }
            />
            <Route
              path="/audit"
              element={
                <RequireAuth>
                  <AuditLog />
                </RequireAuth>
              }
            />
            <Route
              path="/batches/:id/a2-801k"
              element={<RequireAuth><A2801K /></RequireAuth>}
            />
            <Route
              path="/batches/:id/output-log"
              element={<RequireAuth><OutputLog /></RequireAuth>}
            />
            <Route
              path="/batches/:id/a2-801a"
              element={<RequireAuth><A2801A /></RequireAuth>}
            />
            <Route
              path="/batches/:id/a2-801c"
              element={<RequireAuth><A2801C /></RequireAuth>}
            />
            <Route
              path="/batches/:id/qa-134b"
              element={<RequireAuth><QA134B /></RequireAuth>}
            />
            <Route
              path="/admin/users"
              element={
                <RequireAuth>
                  <UserManagement />
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </SentryErrorBoundary>
  )
}
